import { z } from 'zod';

/**
 * The account-detail page's `?tab=` values. Non-component sibling module (not
 * `DetailTabNav.tsx`) so `routes/email-accounts.$id.tsx` — which does not
 * import from feature `.tsx` files — can build its Zod schema off the same
 * list `DetailTabNav` renders, rather than a second hand-typed enum.
 */
export const DETAIL_TABS = ['overview', 'activity', 'analytics', 'warmup-settings'] as const;
export type DetailTab = (typeof DETAIL_TABS)[number];

export const detailTabSchema = z.enum(DETAIL_TABS).optional().catch(undefined);

/** The `?section=` deep-link values `account-status-signals.ts` and the ESP
 * section anchor emit. Kept local to this module rather than importing
 * `StatusSection` from `tanstack/account-status-signals.ts` — that type
 * deliberately excludes `'esp'` (no live signal emits it today; see
 * `routes/email-accounts.$id.tsx`), so this is a superset, not a duplicate. */
export type DetailSection = 'connection' | 'dns' | 'analytics' | 'esp';

/**
 * Maps a `?section=` deep-link to the tab that contains its target anchor.
 * `'analytics'` here is the **Overview** tab's `ReputationTrendCard`
 * (`#section-analytics`) — a pre-existing value from `account-status-signals.ts`
 * that predates the tab split — NOT the new "Analytics" tab. Only `'esp'`
 * (the `EspAnalyticsSection` anchor, `#section-esp`) resolves to the Analytics
 * tab.
 */
export function sectionToTab(section: DetailSection): DetailTab {
  return section === 'esp' ? 'analytics' : 'overview';
}
